import type { EntityBody } from "@cloudnest/kinetic-core-engine";
import type { PieceTypeInitConfig } from "../config/types";
import type { CanvasLayout } from "./coordinate-transform";
import { boardToCanvas } from "./coordinate-transform";
import type { AlkagiCanvasTokens } from "./token-bridge";

const DEFAULT_LABELS = {
  hanLabel: "H",
  choLabel: "C",
} as const;

function getPieceType(
  pieceTypesById: ReadonlyMap<string, PieceTypeInitConfig>,
  archetype: string,
) {
  return pieceTypesById.get(archetype);
}

export function renderBoard(
  ctx: CanvasRenderingContext2D,
  layout: CanvasLayout,
  cols: number,
  rows: number,
  tokens: AlkagiCanvasTokens,
): void {
  ctx.fillStyle = tokens.boardFill;
  ctx.fillRect(0, 0, layout.canvasWidth, layout.canvasHeight);

  ctx.strokeStyle = tokens.gridLine;
  ctx.lineWidth = 1;

  for (let col = 0; col < cols; col++) {
    const start = boardToCanvas(col, 0, layout);
    const end = boardToCanvas(col, rows - 1, layout);
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
  }

  for (let row = 0; row < rows; row++) {
    const start = boardToCanvas(0, row, layout);
    const end = boardToCanvas(cols - 1, row, layout);
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
  }
}

export function renderEntity(
  ctx: CanvasRenderingContext2D,
  entity: EntityBody,
  layout: CanvasLayout,
  isSelected: boolean,
  pieceTypesById: ReadonlyMap<string, PieceTypeInitConfig>,
  ownerOneId: string,
  tokens: AlkagiCanvasTokens,
): void {
  if (entity.eliminated) return;

  const pieceType = getPieceType(pieceTypesById, entity.archetype);
  const labels = pieceType?.design ?? DEFAULT_LABELS;
  const isOwnerOne = entity.owner === ownerOneId;

  const { x, y } = boardToCanvas(entity.x, entity.y, layout);
  const pixelRadius = entity.radius * layout.cellSize;

  // Drop shadow lifts the disc off the wood per the visual spec
  ctx.save();
  ctx.shadowColor = tokens.stoneShadow;
  ctx.shadowBlur = 2;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 1;

  ctx.beginPath();
  ctx.arc(x, y, pixelRadius, 0, Math.PI * 2);
  ctx.fillStyle = isOwnerOne ? tokens.stoneWhite : tokens.stoneBlack;
  ctx.fill();
  ctx.restore();

  // White stones get a thin border so they stay legible on the wood
  if (isOwnerOne) {
    ctx.beginPath();
    ctx.arc(x, y, pixelRadius, 0, Math.PI * 2);
    ctx.lineWidth = 1;
    ctx.strokeStyle = tokens.stoneWhiteBorder;
    ctx.stroke();
  }

  if (isSelected) {
    ctx.beginPath();
    ctx.arc(x, y, pixelRadius + 2, 0, Math.PI * 2);
    ctx.lineWidth = 3;
    ctx.strokeStyle = tokens.stoneSelected;
    ctx.stroke();
  }

  ctx.fillStyle = isOwnerOne ? tokens.stoneBlack : tokens.stoneWhite;
  ctx.font = `bold ${Math.round(pixelRadius * 1.05)}px serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(isOwnerOne ? labels.hanLabel : labels.choLabel, x, y);
}

export function renderEntities(
  ctx: CanvasRenderingContext2D,
  entities: ReadonlyArray<EntityBody>,
  layout: CanvasLayout,
  selectedEntityId: string | null,
  pieceTypesById: ReadonlyMap<string, PieceTypeInitConfig>,
  ownerOneId: string,
  tokens: AlkagiCanvasTokens,
): void {
  for (const entity of entities) {
    renderEntity(
      ctx,
      entity,
      layout,
      entity.id === selectedEntityId,
      pieceTypesById,
      ownerOneId,
      tokens,
    );
  }
}

export function renderFlickPreview(
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  layout: CanvasLayout,
  tokens: AlkagiCanvasTokens,
): void {
  const from = boardToCanvas(fromX, fromY, layout);
  const to = boardToCanvas(toX, toY, layout);

  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.strokeStyle = tokens.aimLine;
  ctx.lineWidth = 3;
  ctx.setLineDash([6, 4]);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.beginPath();
  ctx.arc(to.x, to.y, 6, 0, Math.PI * 2);
  ctx.fillStyle = tokens.aimDot;
  ctx.fill();
}
